import { v4 } from "uuid";
import type { Tokens } from "#lib/frontegg.js";
import type { JwtPayload } from "#lib/jwt.js";
import { workspacePath } from "#lib/path.js";
import { extractDomain } from "#lib/url.js";
import { PUBLIC_COOKIE_NAME } from "$app/env/public";
import type { Cookies } from "@sveltejs/kit";

const SESSION_COOKIE_NAME = "fc_session";
const RETURN_TO_COOKIE_NAME = "fc_return_to";
const LAST_PLATFORM_COOKIE_NAME = "fc_last_platform_used";
const OPT_OUT_COOKIE_NAME = "OPT_OUT";
const ANONYMOUS_SESSION_ID_COOKIE_NAME = "ANONYMOUS_SESSION_ID";

function newCookieId(): string {
	return `${v4()}-SSE:${Date.now()}`;
}

function isSecure(url: URL): boolean {
	return url.hostname !== "localhost";
}

function isOptedOut(cookies: Cookies): boolean {
	return !!cookies.get(OPT_OUT_COOKIE_NAME);
}

export function getTrackingCookieName(): string {
	return PUBLIC_COOKIE_NAME;
}

function getTrackingCookie(cookies: Cookies): string {
	let id = cookies.get(getTrackingCookieName());
	if (!id) {
		id = newCookieId();
	}
	return id;
}

export function setTrackingCookie(url: URL, cookies: Cookies): string | undefined {
	if (isOptedOut(cookies)) {
		return undefined;
	}
	const id = getTrackingCookie(cookies);
	const expires = new Date();
	expires.setFullYear(expires.getFullYear() + 10);

	cookies.set(getTrackingCookieName(), id, {
		secure: isSecure(url),
		domain: url.hostname,
		httpOnly: true,
		path: "/",
		sameSite: "lax",
		expires,
	});
	return id;
}

export function setAnonymousSessionCookie(cookies: Cookies): string | undefined {
	let anonymousSessionId = cookies.get(ANONYMOUS_SESSION_ID_COOKIE_NAME);
	if (!isOptedOut(cookies) && !anonymousSessionId) {
		anonymousSessionId = v4();
		cookies.set(ANONYMOUS_SESSION_ID_COOKIE_NAME, anonymousSessionId, { path: "/" });
	}
	return anonymousSessionId;
}

export function getSessionCookie(cookies: Cookies): string | undefined {
	return cookies.get(SESSION_COOKIE_NAME);
}

export function setSessionCookie(url: URL, cookies: Cookies, tokens: Tokens) {
	cookies.set(SESSION_COOKIE_NAME, JSON.stringify(tokens), {
		httpOnly: true,
		path: "/",
		secure: isSecure(url),
		sameSite: "lax",
		maxAge: 60 * 60 * 24, // 1 day
	});
}

export function deleteSessionCookie(url: URL, cookies: Cookies) {
	cookies.delete(SESSION_COOKIE_NAME, {
		httpOnly: true,
		path: "/",
		secure: isSecure(url),
		sameSite: "lax",
		maxAge: 60 * 60 * 24, // 1 day
	});
}

export function getLastWorkspaceCookie(cookies: Cookies, claims?: JwtPayload): string | undefined {
	if (!claims) {
		return undefined;
	}
	const cookie = cookies.get(`fc_${claims.sub}_workspace`);
	if (cookie) {
		const [orgId, workspaceId] = cookie.split(":");
		return workspacePath(orgId, workspaceId);
	}
	return undefined;
}

export function setLastPlatformUsedCookie(url: URL, cookies: Cookies) {
	cookies.set(LAST_PLATFORM_COOKIE_NAME, "f2", {
		domain: extractDomain(url),
		httpOnly: false,
		path: "/",
		secure: isSecure(url),
		sameSite: "lax",
		expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365), // 1 year
	});
}

export function setLastWorkspaceCookie(url: URL, cookies: Cookies, orgId: string, workspaceId: string, claims?: JwtPayload) {
	if (!claims) {
		return;
	}
	cookies.set(`fc_${claims.sub}_workspace`, `${orgId}:${workspaceId}`, {
		domain: url.hostname,
		httpOnly: false,
		path: "/",
		secure: isSecure(url),
		sameSite: "lax",
		expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365), // 1 year
	});
}

export function deleteReturnToCookie(url: URL, cookies: Cookies) {
	cookies.delete(RETURN_TO_COOKIE_NAME, {
		domain: extractDomain(url),
		httpOnly: true,
		path: "/",
		secure: isSecure(url),
		sameSite: "lax",
	});
}

export function setReturnToCookie(url: URL, cookies: Cookies) {
	cookies.set(RETURN_TO_COOKIE_NAME, url.toString(), {
		domain: extractDomain(url),
		httpOnly: true,
		path: "/",
		secure: isSecure(url),
		sameSite: "lax",
	});
}
